import { useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import { confirmDialog } from '../confirm';
import Expander from './Expander';
import { ChangeRequest, isGenerated } from './ChangeRequest';
import { CharterDocument } from './CharterDocument';
import { RequirementsDocument, isRequirements } from './RequirementsDocument';
import { AfterActionReport } from './AfterActionReport';
import { can, effectiveRole } from '../permissions';
import { displayName, isExternalVendor } from '../people';
import InfoTip from './InfoTip';
import { useCallback, useEffect } from 'react';
import {
  DOCUMENT_KIND,
  DOCUMENT_KIND_WHAT,
  DOCUMENT_GROUPS,
  docSource,
  canScopeToSprint,
  titleForKind,
  TITLE_EXAMPLE,
  NEW_DOC,
  acceptFor,
  covers,
  fileWhat,
  isEditable,
  isReceived,
  isVendorShareable,
  isSignableFile,
  readsOnThePage,
  viewAs,
} from '../documents';
import { DocFace, DocFormHeader } from './DocLine';
import {
  DOC_FIELD_LABEL,
  DOC_SORT_LABEL,
  EMPTY_DOC_FILTER,
  docChoices,
  docCountFor,
  docFilterCount,
  docPasses,
  sortDocs,
  toggleDocFilter,
  type DocFilter,
  type DocFilterField,
  type DocSort,
} from '../doc-filter';
import { asDate } from '../utils';
import { parseCsv, type Sheet } from '../import-csv';
import SignoffSheet from './SignoffSheet';
import { useSampleBoard, useSampleInert } from './SampleStrip';
import Icon from './Icon';
import type { Doc, DocumentKind, Signatory, Signoff } from '../types';
import { seedCharterSignatories } from '../signatories';
import {
  policyForType,
  usesStages,
  usesSlots,
  sheetFor,
  stepLabel,
  sheetGroups,
} from '../signoff-policy';
import type { TheWorld } from '../signoff-policy';

/** The last moment anybody signed — when a signed document became locked. */
const lastSignedAt = (rows: Signoff[]): number | null => {
  const times = rows.map((r) => r.signedAt).filter((t): t is number => !!t);
  return times.length ? Math.max(...times) : null;
};

/** The people who signed, read as a sentence — "Pat Moore", "Pat Moore and
 *  Marcia Alleyne", "Pat Moore, Marcia Alleyne and Dana Cole" — so a signed
 *  document can say who approved it, not just that it was approved. */
const andList = (names: string[]): string => {
  const named = names.filter((n) => n?.trim());
  if (named.length === 0) return '';
  if (named.length === 1) return named[0];
  return `${named.slice(0, -1).join(', ')} and ${named[named.length - 1]}`;
};

/**
 * THE DOCUMENTS A PROJECT IS DELIVERED AGAINST — #193.
 *
 * ONE LIST, BECAUSE THE QUESTION THESE ANSWER IS ASKED LATER AND ALL AT ONCE.
 * A scope of works exists to be produced to somebody who was not there, and
 * "show me everything signed on this project" has to be one screen with dates
 * on it. Putting each document beside the work it describes reads better on the
 * day and cannot answer that question at all without opening things one by one.
 *
 * EVERY ROW SAYS WHAT IT COVERS. Vinnie, 21 August: approval is given for a
 * grouped set of work, not a card — "I don't think any developer only gets
 * approval to work on one issue or one card, it would be a waste of time". So a
 * document covers a sprint or the whole project, the row says which, and there
 * is deliberately no document on a card anywhere in this app.
 */
export default function Documents() {
  const documents = useApp((s) => s.documents);
  const sprints = useApp((s) => s.sprints);
  const role = useApp((s) => s.shownRole);
  const search = useApp((s) => s.search);
  const draftDocument = useApp((s) => s.draftDocument);
  const drillTo = useApp((s) => s.drillTo);

  /*
   * TWO RANKS, AND THEY ARE NOT THE SAME QUESTION. Anyone who may edit cards
   * may write a draft; sending one for signature is the rank that cuts a
   * release. Migration 0044 enforces both, and asking here as well keeps a
   * control off the screen rather than offering one the write will refuse.
   */
  const mayDraft = can(role, 'editIssue');
  const mayAsk = can(role, 'editSettings');
  // A VENDOR SEES ONLY WHAT WAS SHARED — the same screen, narrowed (B-05). The list
  // already comes filtered from the server (documents_read, 0196/0197), so there is
  // nothing to hide here; what changes is the chrome around it: no sharing column, no
  // "New document" (a vendor reads and downloads, they do not author documents), and
  // a count line that speaks to them. `mayShare` is who may flip the flag — manager+.
  const isVendor = role === 'vendor';
  const mayShare = mayAsk && !isVendor;
  const inert = useSampleInert();

  // VENDOR SHARING, MADE DISCOVERABLE BEFORE A DOCUMENT EXISTS (B-04 follow-up).
  // The "Visible to vendors" switch lives on a document ROW, so a board with no
  // documents gives no sign the capability is there — Vinnie got stuck exactly
  // there. These drive an empty-state note and a quiet nudge; both are for
  // managers only (mayShare), and both name the vendor(s) actually on the board.
  const access = useApp((s) => s.access);
  const roles = useApp((s) => s.roles);
  const legacyRoles = useApp((s) => s.legacyRoles);
  const projectRoles = useApp((s) => s.projectRoles);
  const profiles = useApp((s) => s.profiles);
  const boardId = useApp((s) => s.projectId);
  // Whoever's effective role ON THIS BOARD is the vendor tier — the same role
  // source the Access screen reads, so the two never disagree.
  const vendorsHere = useMemo(
    () =>
      access.emails.filter(
        (e) =>
          effectiveRole({
            email: e,
            admins: access.admins,
            defaults: roles,
            on: projectRoles,
            legacy: legacyRoles,
            projectId: boardId,
          }) === 'vendor'
      ),
    [access.emails, access.admins, roles, projectRoles, legacyRoles, boardId]
  );
  // How to name them in a sentence: one by name, more than one by count.
  const vendorLabel =
    vendorsHere.length === 1 ? displayName(vendorsHere[0], profiles) : `${vendorsHere.length} vendors`;
  const vendorIsAre = vendorsHere.length === 1 ? 'is' : 'are';
  const sharedCount = documents.filter((d) => d.visibleToVendors).length;
  // Whether the WORKSPACE has any vendor at all (a person marked as the vendor
  // company role in workspace settings) — Vinnie, 23 Oct: the empty-state block is
  // worth showing only once there is a vendor to share with, not on every board.
  const workspaceHasVendor = useMemo(
    () => access.emails.some((e) => isExternalVendor(e, profiles)),
    [access.emails, profiles]
  );
  // The shared detail that lives in every "i" on this topic — kept in one place.
  const shareTip = (
    <>
      The switch sits in a <b>Visible to vendors</b> column that appears once your first document
      exists. Only a manager or admin can flip it, and you share one document at a time.
    </>
  );

  const [adding, setAdding] = useState(false);
  const [open, setOpen] = useState('');
  const [problem, setProblem] = useState('');

  /* HOW THE LIST IS ARRANGED — the same idea the backlog runs, so the two feel
     like one product. Newest first by default, and no filter until one is set. */
  const [sort, setSort] = useState<DocSort>('newest');
  const [filter, setFilter] = useState<DocFilter>(EMPTY_DOC_FILTER);
  /* The settled documents — signed off and superseded — are folded away by
     default; the live work is what a person came to see. */
  const [settledOpen, setSettledOpen] = useState(false);

  /* OPENED FROM ELSEWHERE — the sprint's "Open the change request" link drills
     here with the document's id. Open it and bring it into view, the same way
     Sprints reveals a sprint.

     ⚠️ WAIT FOR THE DOCUMENT TO ARRIVE. When the sprint has just CREATED it, the
     drill lands here before the realtime insert has reached the store, so the
     row does not exist yet and opening it would do nothing. So this waits until
     the document is actually in the list, then opens it once — tracked by a ref
     so collapsing it by hand afterwards is not fought on the next render. */
  const revealId = useApp((s) => s.revealId);
  const revealed = useRef('');
  useEffect(() => {
    if (!revealId || revealed.current === revealId) return;
    if (!documents.some((d) => d.id === revealId)) return;
    revealed.current = revealId;
    setOpen(revealId);
    document.getElementById(`doc-${revealId}`)?.scrollIntoView({ block: 'center' });
  }, [revealId, documents]);

  /* ARRIVED TO MAKE ONE. The Plan's "New document" button drills here with the
     NEW_DOC marker instead of a document id, so the button does what it says —
     it opens the form, rather than dropping the reader on the list to find it.
     Fired once, then left alone so closing the form by hand is not fought. */
  const askedNew = useRef(false);
  useEffect(() => {
    if (revealId !== NEW_DOC || askedNew.current) return;
    askedNew.current = true;
    setAdding(true);
  }, [revealId]);

  /* A DOCUMENT OPENS ON ITS OWN — Vinnie, 15 September 2026: a document expanded
     inline pushed the rest of the list down the page, and a long one (a generated
     requirements document runs to hundreds of lines) buried everything else, so
     you scrolled through one document to reach the next. Opening it on its own
     panel, with a way back, lets a person read just the one. Escape is the same
     way out the rest of the app gives anything opened over the screen. */
  useEffect(() => {
    if (!open) return;
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen('');
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [open]);

  const sprintName = useMemo(() => {
    const by = new Map(sprints.map((s) => [s.id, s.name]));
    return (id: string) => by.get(id);
  }, [sprints]);

  const q = search.trim().toLowerCase();
  /* THREE THINGS NARROW THE LIST, and they answer different questions. The
     global ⌘K search matches words; the Filter narrows by kind, status or what
     a document covers; the Sort arranges what is left. Search and Filter are
     both applied, then the result is sorted — so a filtered list still reads
     top-to-bottom in the order the person asked for. */
  const matched = q
    ? documents.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          DOCUMENT_KIND[d.kind].toLowerCase().includes(q) ||
          d.body.toLowerCase().includes(q)
      )
    : documents;
  const visible = matched.filter((d) => docPasses(d, filter));

  const waiting = documents.filter((d) => d.state === 'awaiting').length;

  /* THE ONE THAT IS OPEN, ON ITS OWN. Read from the whole list, not the filtered
     one, so narrowing the list underneath never pulls the open document out from
     under the reader. Gone (deleted, or superseded into a new row) falls back to
     the list rather than a blank panel. */
  const openDoc = open ? documents.find((d) => d.id === open) : undefined;
  if (openDoc) {
    return (
      <div className="doc-screen doc-screen-focused">
        <button className="btn btn-small doc-back" onClick={() => setOpen('')}>
          ← Documents
        </button>
        <Row
          doc={openDoc}
          covers={covers(openDoc, sprintName)}
          focused
          mayAsk={mayAsk}
          mayDraft={mayDraft}
        />
      </div>
    );
  }

  return (
    <div className="doc-screen">
      <div className="doc-head">
        <div>
          {/* No page title here — the topbar already names this screen
              "Documents"; a second heading right beneath it was the same word
              twice (and two page headings on one screen). The count line stands
              on its own. */}
          <p className="doc-count">
            {isVendor
              ? documents.length === 0
                ? 'Nothing has been shared with you yet.'
                : `${documents.length} ${documents.length === 1 ? 'document' : 'documents'} shared with you`
              : documents.length === 0
                ? 'Nothing kept here yet.'
                : `${documents.length} on this project` +
                  (waiting > 0
                    ? ` · ${waiting} awaiting ${waiting === 1 ? 'a signature' : 'signatures'}`
                    : '')}
          </p>
        </div>
        {mayDraft && !isVendor && (
          <button data-reveal="new-document" {...inert('btn btn-small', () => setAdding(true))}>
            New document
          </button>
        )}
      </div>

      {/* A VENDOR IS ON THE BOARD BUT SEES NOTHING YET — sharing is opt-in and
          silent, so without this a manager who invited a vendor expecting them to
          read the spec has shared nothing and gets no signal. Shown only to someone
          who may share, only while a vendor is on the board AND nothing is shared,
          and only once there are documents (with none, the empty-state block above
          carries the same message, so this never doubles up). It self-clears the
          moment any row is shared. A vendor never sees it. */}
      {mayShare && vendorsHere.length > 0 && sharedCount === 0 && documents.length > 0 && !adding && (
        <div className="doc-vnudge">
          <Icon name="person" size={15} className="doc-vnudge-ico" />
          <span>
            <b>{vendorLabel}</b> {vendorIsAre} on this board but can’t see any documents yet. Flip{' '}
            <b>Visible to vendors</b> on a row to share one with them.
          </span>
          <InfoTip label="Sharing documents with vendors">{shareTip}</InfoTip>
        </div>
      )}

      {adding && (
        <NewDocument
          onDone={() => setAdding(false)}
          onProblem={setProblem}
          create={draftDocument}
          documents={documents}
          onOpen={(id) => {
            setAdding(false);
            setProblem('');
            /* OPEN IT THROUGH THE DURABLE REVEAL, not a bare local setOpen —
               Vinnie, 1 Oct: creating a document flashed the editor open then
               dropped back to the list with the row in draft. `setOpen(id)` is
               local state; the realtime echo that lands a beat later replaces
               `documents` and re-runs this screen, and the open was lost before
               the just-made row had settled in the list. `drillTo` plants the
               id in the store's revealId, and the reveal effect above opens it
               once the row is actually present — the same path a sprint-created
               document already uses, which survives the echo and any re-render. */
            drillTo('documents', id);
          }}
        />
      )}

      {problem && (
        <p className="doc-problem" role="alert">
          {problem}
        </p>
      )}

      {/*
        THE EMPTY STATE SAYS WHAT THIS IS FOR, not that there is nothing here.
        A project with no documents is the ordinary case for every customer who
        does not work this way, so it must not read as something missing.
      */}
      {documents.length === 0 && !adding && isVendor && (
        <div className="doc-empty">
          {/* A VENDOR SEES ONLY WHAT WAS SHARED (B-05), so an empty list means nothing
              has been handed to them yet — not that the project has no documents. The
              internal copy below (what a document is, who signs it) would be wrong
              and confusing here. */}
          <p>Nothing has been shared with you yet.</p>
          <p className="doc-empty-more">
            When your project contact shares the spec or a test script for this work, it appears here
            to read and download.
          </p>
        </div>
      )}
      {documents.length === 0 && !adding && !isVendor && (
        <div className="doc-empty">
          <p>
            A document is what a piece of work is signed off against — a statement of work, a change
            request, or a test script after a sprint.
          </p>
          <p className="doc-empty-more">
            Each one covers a sprint or the whole project, and is signed by the people named in
            Settings.
          </p>
          {/* The one next action, right here — the empty state used to explain what
              a document is but leave a newcomer with nowhere to start (F4). Same
              handler as the header's New document button. */}
          {mayDraft && (
            <button data-reveal="new-document" {...inert('btn btn-primary doc-empty-cta', () => setAdding(true))}>
              New document
            </button>
          )}
          {/* SHARING WITH VENDORS, SHOWN BEFORE ANY DOCUMENT EXISTS — the per-row
              switch is invisible with an empty list, so the empty state teaches it:
              what you do, in order; where the switch will sit; and (in the "i") who
              may flip it. Managers only. When a vendor is already on the board, a
              leading sentence says so, so the second nudge below never also fires. */}
          {mayShare && workspaceHasVendor && (
            <div className="doc-empty-vsec">
              <div className="grp-heading doc-empty-vhead">
                <Icon name="lock" size={14} className="doc-empty-vico" />
                <span>Sharing with vendors</span>
                <InfoTip label="Sharing documents with vendors">{shareTip}</InfoTip>
              </div>
              <p className="doc-empty-vbody">
                {vendorsHere.length > 0 && (
                  <>
                    <b>
                      {vendorLabel} {vendorIsAre} on this board and can’t see any documents yet.
                    </b>{' '}
                  </>
                )}
                Create a document, then flip <b>Visible to vendors</b> on its row to let{' '}
                {vendorsHere.length > 0 ? 'them' : 'a vendor'} — an outside supplier you have given
                board access — read and download that one. Each document starts Internal.
              </p>
              <div className="doc-empty-vpreview">
                <span className="doc-empty-vpl">The switch that appears on each row:</span>
                <span className="doc-share">
                  <span className="doc-share-word">Internal</span>
                  <span className="switch" aria-hidden="true">
                    <span className="switch-knob" />
                  </span>
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ARRANGE THE LIST — only once there is a list to arrange. Two documents
          is where sorting starts to earn its place; below that it is a control
          with nothing to do. The global ⌘K search is left to do word-matching;
          this is narrowing and ordering, which search does not do. */}
      {documents.length > 1 && (
        <DocTools
          docs={matched}
          sprintName={sprintName}
          filter={filter}
          onFilter={setFilter}
          sort={sort}
          onSort={setSort}
        />
      )}

      {visible.length === 0 && documents.length > 0 && (
        <p className="doc-empty">
          {q && docFilterCount(filter) > 0
            ? `Nothing matches “${search.trim()}” with these filters.`
            : q
            ? `Nothing here matches “${search.trim()}”.`
            : 'Nothing matches these filters.'}
        </p>
      )}

      {/*
        TWO GROUPS, BUT ONLY ONCE THERE ARE TWO. What a project is HANDED —
        a scope of work, its plan, an attachment — is kept and read; what it
        PRODUCES is sent round to be signed. They read differently and get
        different controls, so they sit apart. A project with only one sort
        keeps the single plain list it always had — a heading over a list with
        nothing to contrast it is just clutter. See migration 0111.

        AND THE SETTLED WORK FOLDS AWAY. Within what a project produces, the
        signed-off and superseded documents are the record — worth keeping, not
        worth scrolling past every visit. They collapse behind a disclosure that
        opens on a click, so the live drafts and the documents out for signature
        are what meets the eye. The received/reference documents never settle —
        they are kept and read as they are — so they are never folded.
      */}
      {visible.length > 0 &&
        (() => {
          const received = sortDocs(
            visible.filter((d) => isReceived(d.kind)),
            sort
          );
          const forSignoff = visible.filter((d) => !isReceived(d.kind));
          const live = sortDocs(
            forSignoff.filter((d) => d.state === 'draft' || d.state === 'awaiting'),
            sort
          );
          const settled = sortDocs(
            forSignoff.filter((d) => d.state === 'signed' || d.state === 'superseded'),
            sort
          );
          /* WHILE A FILTER IS ON, NOTHING FOLDS. The Settled fold is a browsing
             convenience — it keeps the settled record out of the way when you
             are just looking down the list. But once you have explicitly
             filtered (say, to "Signed off"), folding the matches away would make
             the filter look like it returned nothing. So a filtered sign-off
             list is one flat, sorted list; the fold returns when the filter is
             cleared. */
          const filtering = docFilterCount(filter) > 0;
          const flatSignoff = sortDocs(forSignoff, sort);
          /* A heading only earns its place when there is a second group to tell
             it apart from. One kind of document keeps the single plain list it
             always had. */
          const groups = (received.length > 0 ? 1 : 0) + (forSignoff.length > 0 ? 1 : 0);
          const labelled = groups > 1;
          const row = (doc: Doc) => (
            <Row
              key={doc.id}
              doc={doc}
              covers={covers(doc, sprintName)}
              onOpen={() => setOpen(doc.id)}
              mayAsk={mayAsk}
              mayDraft={mayDraft}
              showVendor={!isVendor}
              mayShare={mayShare}
            />
          );
          const signedN = settled.filter((d) => d.state === 'signed').length;
          const supersededN = settled.filter((d) => d.state === 'superseded').length;
          const settledHint =
            supersededN === 0
              ? `${signedN} signed off`
              : signedN === 0
              ? `${supersededN} superseded`
              : `${signedN} signed off, ${supersededN} superseded`;
          return (
            <div className="doc-table">
              {/* THE COLUMN HEADER — the only one on this screen, added with the
                  Visible-to-vendors column so the new cell reads as part of the table
                  (B-04). It reuses the row cells' own classes, so the labels sit in
                  the same columns as the values beneath them. Not shown to a vendor:
                  their list has no sharing column. */}
              {!isVendor && (
                <div className="doc-colhead" aria-hidden="true">
                  <div className="doc-colhead-main">
                    <span className="doc-kind">Type</span>
                    <span className="doc-title">Document</span>
                    <span className="doc-covers">Covers</span>
                    <span className="doc-date">Made</span>
                    <span className="doc-status">Status</span>
                  </div>
                  <span className="doc-vendor">Visible to vendors</span>
                </div>
              )}
              {/* One framed table: each group's name is an in-table band row above
                  its own rows (the header band governs every group). One kind of
                  document keeps a single unlabelled section — no band row. */}
              {received.length > 0 && (
                <>
                  {labelled && <h3 className="doc-group">Received &amp; Reference</h3>}
                  {received.map(row)}
                </>
              )}
              {forSignoff.length > 0 && (
                <>
                  {labelled && <h3 className="doc-group">Sent for Sign-Off</h3>}
                  {filtering ? (
                      flatSignoff.map(row)
                    ) : (
                      <>
                        {live.map(row)}
                        {settled.length > 0 && (
                          <>
                            <button
                              className={`doc-settled${settledOpen ? ' is-open' : ''}`}
                              aria-expanded={settledOpen}
                              onClick={() => setSettledOpen(!settledOpen)}
                            >
                              <span className="doc-settled-chev" aria-hidden="true">
                                <Icon name="chevron" size={18} />
                              </span>
                              <span className="doc-settled-label">Settled</span>
                              <span className="doc-settled-hint">{settledHint}</span>
                            </button>
                            <Expander open={settledOpen}>{settled.map(row)}</Expander>
                          </>
                        )}
                      </>
                    )}
                </>
              )}
            </div>
          );
        })()}
    </div>
  );
}

/**
 * FILTER AND SORT FOR THE DOCUMENTS LIST — the backlog's controls, in the shape
 * a document list needs. Two quiet buttons that each drop a menu directly
 * beneath themselves (the `.dd` wrapper anchors it), reusing the backlog's
 * `.bf-menu` markup so the two screens are one product. Filter drills a field,
 * then its values with live counts and ticks; Sort is a single-select list.
 *
 * DRAWN AS ITS OWN PIECE, and for the backlog's reason: every menu here opens on
 * a click, and the screen harness cannot click — inline, these menus would be
 * the one part of the screen nothing renders and nobody reviews.
 */
function DocTools({
  docs,
  sprintName,
  filter,
  onFilter,
  sort,
  onSort,
}: {
  docs: Doc[];
  sprintName: (id: string) => string | undefined;
  filter: DocFilter;
  onFilter: (f: DocFilter) => void;
  sort: DocSort;
  onSort: (s: DocSort) => void;
}) {
  const [menu, setMenu] = useState<'' | 'filter' | 'sort'>('');
  const [field, setField] = useState<DocFilterField | ''>('');

  /* ANYWHERE OUTSIDE CLOSES IT, and so does Escape — the same close the backlog
     learned the hard way (a menu that only the button could dismiss left a
     click on a row underneath doing nothing). Asked of the toolbar itself, so
     a click on either button or inside either menu keeps it open, and picking a
     filter value does NOT close it — choosing two kinds is one action. */
  useEffect(() => {
    if (!menu) return;
    const away = (e: MouseEvent) => {
      const hit = e.target as Element | null;
      if (!hit?.closest?.('.doc-tools')) setMenu('');
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenu('');
    };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', key);
    };
  }, [menu]);

  const on = (f: DocFilterField) => filter[f];
  const nFields = docFilterCount(filter);

  return (
    <div className="doc-tools">
      <div className="dd">
        <button
          className={`btn btn-small${menu === 'filter' ? ' bf-open' : ''}`}
          aria-expanded={menu === 'filter'}
          onClick={() => {
            setMenu(menu === 'filter' ? '' : 'filter');
            setField('');
          }}
        >
          Filter
          {nFields > 0 && <span className="bf-n">{nFields}</span>}
        </button>
        {menu === 'filter' && (
          <div className="bf-menu" role="dialog" aria-label="Filter documents">
            {!field ? (
              <>
                <p className="bf-menu-head">Filter by…</p>
                <ul className="bf-fields">
                  {(Object.keys(DOC_FIELD_LABEL) as DocFilterField[]).map((f) => (
                    <li key={f}>
                      <button onClick={() => setField(f)}>
                        {DOC_FIELD_LABEL[f]}
                        {on(f).length > 0 && <span className="bf-n">{on(f).length}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <p className="bf-menu-head">
                  <button className="bf-back" onClick={() => setField('')}>
                    ‹ Back
                  </button>
                  {DOC_FIELD_LABEL[field]}
                </p>
                <ul className="bf-values">
                  {docChoices(field, docs, sprintName).map((c) => (
                    <li key={c.value}>
                      <button
                        aria-pressed={on(field).includes(c.value)}
                        onClick={() => onFilter(toggleDocFilter(filter, field, c.value))}
                      >
                        <span className="bf-tick" aria-hidden="true">
                          {on(field).includes(c.value) ? '✓' : ''}
                        </span>
                        {c.label}
                        <span className="bf-n">{docCountFor(field, c.value, docs, filter)}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>

      <div className="dd">
        <button
          className={`btn btn-small${menu === 'sort' ? ' bf-open' : ''}`}
          aria-expanded={menu === 'sort'}
          onClick={() => setMenu(menu === 'sort' ? '' : 'sort')}
        >
          Sort
        </button>
        {menu === 'sort' && (
          <div className="bf-menu" role="dialog" aria-label="Sort documents">
            <p className="bf-menu-head">Sort</p>
            <ul className="bf-values">
              {(Object.keys(DOC_SORT_LABEL) as DocSort[]).map((k) => (
                <li key={k}>
                  <button
                    aria-pressed={sort === k}
                    onClick={() => {
                      onSort(k);
                      setMenu('');
                    }}
                  >
                    <span className="bf-tick" aria-hidden="true">
                      {sort === k ? '✓' : ''}
                    </span>
                    {DOC_SORT_LABEL[k]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

/** One document in the list, and everything about it when it is opened. */
function Row({
  doc,
  covers: coversText,
  focused = false,
  onOpen,
  mayAsk,
  mayDraft,
  showVendor = false,
  mayShare = false,
}: {
  doc: Doc;
  covers: string;
  /* Off in the list — the row is a button that OPENS the document on its own
     panel; on in that focused panel, where the whole document is laid out. */
  focused?: boolean;
  onOpen?: () => void;
  mayAsk: boolean;
  mayDraft: boolean;
  /* The Visible-to-vendors cell beside the row — on the internal side, off on a
     vendor's own list (B-04/B-05). */
  showVendor?: boolean;
  /* Whether this viewer may actually flip the flag (manager+). When false the cell
     shows the state without a switch to change it. */
  mayShare?: boolean;
}) {
  const rows = useApp((s) => s.signoffs).filter((r) => r.documentId === doc.id);
  const projectName = useApp((s) => s.projects.find((p) => p.id === s.projectId)?.name);
  const role = useApp((s) => s.shownRole);
  const sample = useSampleBoard();
  const inert = useSampleInert();
  const signoffPolicy = useApp((s) => s.meta.signoffPolicy);
  const saveDocument = useApp((s) => s.saveDocument);
  const setVendorVisibility = useApp((s) => s.setDocVendorVisibility);
  const sendForSignature = useApp((s) => s.sendDocumentForSignature);
  const withdraw = useApp((s) => s.withdrawDocument);
  const binIt = useApp((s) => s.deleteDocument);
  const supersede = useApp((s) => s.supersedeDocument);
  const markSigned = useApp((s) => s.markDocumentSigned);
  const dismissRejected = useApp((s) => s.dismissDocument);
  const drill = useApp((s) => s.drillTo);

  const [body, setBody] = useState(doc.body);
  const [busy, setBusy] = useState('');
  const [problem, setProblem] = useState('');

  const signed = rows.filter((r) => r.signedAt).length;
  const refused = rows.filter((r) => r.answer === 'rejected');
  /* Who actually put their name to it — the register fact a signed document owes
     anyone who finds it later. De-duped: one person who signs in two stages put
     their name to it once as far as the reader is concerned. */
  const signedBy = andList([...new Set(rows.filter((r) => r.signedAt).map((r) => r.name))]);
  const editable = isEditable(doc);
  /* A VENDOR READS AND DOWNLOADS A SHARED DOCUMENT, NEVER AUTHORS IT. The
     database already refuses a vendor every document write (documents_change /
     documents_bin need rank ≥ 3), so offering them the produce / upload / note /
     delete / send-for-sign-off controls only teaches them that a visible control
     is refused — the "a control that would only be refused is not shown" rule.
     `editIssue` (which a vendor has, for cards) is the wrong gate for document
     authoring; this is the right one. */
  const isVendor = role === 'vendor';
  const mayEditDoc = mayDraft && !isVendor;
  /* A received document is kept and read, never signed here — so it wears a
     "Received" tag in place of a sign-off state, and is offered none of the
     signing controls below. See isReceived / migration 0111. */
  const received = isReceived(doc.kind);

  /* THE VISIBLE-TO-VENDORS CELL (B-04) — beside the row, never inside its button,
     so the whole line still opens the document while the control is its own thing.
     A shareable kind gets the app's switch and a one-word state; a never-shareable
     kind (a budget or payment document — none exist today) gets a plain Internal-only
     statement, not a disabled toggle to poke at. Someone who may see the board but not
     share (below manager) gets the state in words, read-only. The database enforces who
     may share (0198); the shareable rule itself is the one source of truth
     NEVER_SHAREABLE_WITH_VENDORS in documents.ts. */
  const shareable = isVendorShareable(doc.kind);
  const shared = !!doc.visibleToVendors;
  const flipShare = async () => {
    const why = await setVendorVisibility(doc.id, !shared);
    if (why) setProblem(why);
  };
  const vendorCell = !showVendor ? null : !shareable ? (
    <span className="doc-internal" title="Governance documents are internal and are never shared with vendors.">
      <Icon name="lock" size={11} className="doc-internal-lock" />
      Internal only
    </span>
  ) : mayShare ? (
    <span className="doc-share">
      <span className={`doc-share-word${shared ? ' is-on' : ''}`}>{shared ? 'Shared' : 'Internal'}</span>
      <button
        className={`switch ${shared ? 'on' : ''}`}
        role="switch"
        aria-checked={shared}
        aria-label={`${shared ? 'Stop sharing' : 'Share'} “${doc.title}” with vendors`}
        onClick={() => void flipShare()}
      >
        <span className="switch-knob" />
      </button>
    </span>
  ) : (
    <span className={`doc-share-ro${shared ? ' is-on' : ''}`}>{shared ? 'Shared' : 'Internal'}</span>
  );

  return (
    <div className={`doc-row is-${doc.state}${focused ? ' is-focused' : ''}`} id={`doc-${doc.id}`}>
      {/* In the list the whole line is a button that opens the document on its
          own (DocFace, the aligned-column row); in the focused panel the document
          wears its titled header (DocFormHeader), not another grid row. The sharing
          control sits in its own cell BESIDE the button (never nested in it). */}
      {!focused && (
        <div className="doc-line-row">
          <button className="doc-line" onClick={onOpen}>
            <DocFace doc={doc} covers={coversText} rejected={refused.length > 0} />
          </button>
          {showVendor && <span className="doc-vendor">{vendorCell}</span>}
        </div>
      )}

      {focused && (
        <div className="doc-open">
          <DocFormHeader doc={doc} covers={coversText} rejected={refused.length > 0} project={projectName} />
          {/*
            * WHAT THIS DOCUMENT IS, AND WHERE IT STANDS — the register line
            * (Vinnie, 16 September 2026: a document must tell whoever finds it what
            * it is, when it was signed off and by whom). A received document says
            * whether a file has actually arrived; anything else says its standing.
            * The signed case is carried by the locked stamp just below, which now
            * also names who approved it.
            */}
          {received && (
            <p className={`doc-standing${doc.filePath ? '' : ' is-awaiting'}`}>
              {doc.filePath
                ? `Received${doc.uploadedAt ? ` on ${asDate(doc.uploadedAt)}` : ''}.`
                : 'Awaiting a file — nothing has been uploaded yet. Add the document below to keep it here.'}
            </p>
          )}
          {/*
            * SIGNED OFF AND LOCKED, SAID ON THE DOCUMENT ITSELF — Vinnie,
            * 7 September 2026: "there is nothing on the document that records
            * approval and locks the document after authorization". This records
            * that it is now closed, WHO approved it and when, and why nothing on it
            * can change. Shown first, because it is the first thing true about a
            * signed document.
            */}
          {doc.state === 'signed' && (
            <p className="doc-locked" role="status">
              <strong>Signed off &amp; locked.</strong> This is the version that was approved
              {lastSignedAt(rows) ? ` on ${asDate(lastSignedAt(rows)!)}` : ''}
              {signedBy ? ` by ${signedBy}` : ''}. It can no longer be
              changed — to revise it, supersede it with a new version.
            </p>
          )}
          {/*
            * A GENERATED DOCUMENT IS WRITTEN HERE, AND STILL BECOMES A FILE.
            *
            * Above the file rather than instead of it: #200's rule stands —
            * nothing is sent for signature without one — so the change request
            * is written, produced as a PDF, and from that point on it is an
            * ordinary document with a file attached. Vinnie, 22 August 2026:
            * *"nothing goes out empty, you have to upload something before it
            * can be sent to get signed-off."*
            */}
          {isGenerated(doc) && (
            <ChangeRequest doc={doc} mayEdit={editable && mayEditDoc && !sample.readOnly} onProblem={setProblem} />
          )}
          {doc.kind === 'charter' && (
            <CharterDocument doc={doc} mayEdit={editable && mayEditDoc && !sample.readOnly} onProblem={setProblem} />
          )}
          {/*
            * REQUIREMENTS IS GENERATED, THE SAME WAY THE CHANGE REQUEST IS —
            * documents programme, increment 2. The document is assembled from the
            * backlog and the charter and read live; the file below is still the
            * copy that gets signed, once one is produced or uploaded.
            */}
          {isRequirements(doc) && (
            <RequirementsDocument doc={doc} mayEdit={editable && mayEditDoc && !sample.readOnly} onProblem={setProblem} />
          )}
          {/*
            * THE AFTER-ACTION REPORT — the closing retrospective, composed from
            * the board at creation and then edited (3.19 spine, A1). Like the
            * charter it is written here and produced as a PDF; the file below is
            * still the copy that gets filed.
            */}
          {doc.kind === 'aar' && (
            <AfterActionReport doc={doc} mayEdit={editable && mayEditDoc && !sample.readOnly} onProblem={setProblem} />
          )}
          <TheFile doc={doc} mayReplace={editable && mayEditDoc} onProblem={setProblem} />

          {editable && mayEditDoc && (
            <label className="stack doc-note">
              A note about it <span className="hint">— the file is the document</span>
              <textarea
                className="input"
                value={body}
                rows={2}
                placeholder="Anything worth saying beside it."
                onChange={(e) => setBody(e.target.value)}
                onBlur={async () => {
                  if (body === doc.body) return;
                  setProblem(await saveDocument(doc.id, { body }));
                }}
                readOnly={sample.readOnly}
              />
            </label>
          )}
          {!editable && doc.body && <p className="doc-note-said">{doc.body}</p>}

          {/*
            * OFFERED ONLY ONCE THERE IS SOMETHING TO SIGN.
            *
            * The database refuses an empty document — 0049 — and offering the
            * button anyway would have made that refusal the way people found
            * out. It is the same rule the release gate keeps: a control that
            * exists to be refused teaches people to stop reading refusals. The
            * walk caught this; the button was there and the write was not.
            */}
          {!received && doc.state === 'draft' && mayAsk && !doc.filePath && (
            <p className="doc-hint doc-act">
              {isGenerated(doc)
                ? 'Produce the PDF before it can be sent for signature.'
                : doc.kind === 'charter'
                ? 'Produce the PDF from the charter, or upload one, before it can be sent for signature.'
                : isRequirements(doc)
                ? 'Produce the PDF from the requirements, or upload one, before it can be sent for signature.'
                : 'Upload the document before it can be sent for signature.'}
            </p>
          )}
          {/* A CHARTER NAMES ITS OWN SIGNATORIES HERE — its sponsor and whoever
              else must approve it, not the board's delivery panel (#1). Every
              other kind is copied onto the panel named in Settings, unchanged. */}
          {doc.state === 'draft' && mayAsk && doc.filePath && doc.kind === 'charter' && (
            <CharterSignoff doc={doc} onProblem={setProblem} />
          )}
          {!received && doc.state === 'draft' && mayAsk && doc.filePath && doc.kind !== 'charter' && isSignableFile(doc) && (
            <div className="doc-act">
              <button
                disabled={busy === 'send'}
                {...inert('btn btn-small', async () => {
                  setBusy('send');
                  setProblem(await sendForSignature(doc.id));
                  setBusy('');
                })}
              >
                {busy === 'send' ? 'Asking…' : 'Send for sign-off'}
              </button>
              <span className="doc-hint">
                The people named in Settings are copied onto it and cannot be changed afterwards.
              </span>
            </div>
          )}
          {/* An uploaded Test Script may be an Excel file (Vinnie, 15 Oct 2026). A
              spreadsheet has no in-app view a signatory could read, so it is kept
              for the record and not offered for sign-off — a PDF or Word one is. */}
          {!received && doc.state === 'draft' && mayAsk && doc.filePath && doc.kind !== 'charter' && !isSignableFile(doc) && (
            <p className="doc-hint doc-act">
              This {fileWhat(doc.fileType) || 'file'} is kept for the record — it cannot be sent for sign-off. Upload a
              PDF or Word version to get it signed.
            </p>
          )}

          {rows.length > 0 && (
            <>
              <p
                className={`doc-progress ${
                  refused.length > 0 ? 'is-refused' : signed === rows.length ? 'is-done' : 'is-waiting'
                }`}
              >
                <Icon
                  name={refused.length > 0 ? 'warning' : signed === rows.length ? 'check' : 'clock'}
                  size={16}
                />
                <span>
                {refused.length > 0 ? (
                  <>
                    <strong>
                      {refused.length === 1 ? `${refused[0].name} rejected this.` : `${refused.length} people rejected this.`}
                    </strong>{' '}
                    Read what they said, then take it back to a draft and answer them.
                  </>
                ) : signed === rows.length ? (
                  <>
                    <strong>Every signature is in.</strong> All {rows.length} signed.
                  </>
                ) : (
                  <>
                    <strong>Not yet signed off.</strong> {signed} of {rows.length} signed.
                  </>
                )}
                </span>
              </p>
              {/* A stage sheet shows its stage headings, in the frozen order; a
                  legacy document (and a charter, which is out of the stage model)
                  draws flat exactly as it always has — see SignoffSheet. */}
              <SignoffSheet
                rows={rows}
                policy={signoffPolicy}
                mayActFor={can(role, 'answerForSomebody')}
                maySendLink={can(role, 'sendSigningLink')}
                locked={doc.state === 'superseded'}
              />
            </>
          )}

          {/*
            * A NEW VERSION, WHICH IS THE ONLY WAY A SIGNED ONE CHANGES — Vinnie,
            * 7 September 2026. The locked stamp and the sprint's drift warning
            * both say "supersede it with a new version"; this is the button that
            * does it. It starts a fresh draft carrying the narratives, leaves
            * this one as the record, and opens the new one to be produced and
            * sent. Manager-only, the same as sending — the database refuses it
            * to anyone else, so a control that would only be refused is not shown.
            */}
          {doc.state === 'signed' && mayAsk && (
            <div className="doc-act">
              <button
                disabled={busy === 'supersede'}
                {...inert('btn btn-small', async () => {
                  setBusy('supersede');
                  const { id, problem: why } = await supersede(doc.id);
                  setBusy('');
                  if (why) setProblem(why);
                  else if (id) drill('documents', id);
                })}
              >
                {busy === 'supersede' ? 'Creating…' : 'Supersede with a new version'}
              </button>
              <span className="doc-hint">
                Starts a new draft that replaces this one. This version stays exactly as it was
                signed.
              </span>
            </div>
          )}

          {/*
            * ANSWERING A REFUSAL — the way out of a rejected request, and the
            * dead-end Vinnie's walk would hit without it. A rejected document
            * sits at `awaiting` (there is no "rejected" state), so Supersede —
            * which only acts on a SIGNED document — is not offered, and plain
            * Withdraw is refused the moment anybody had already approved. That
            * left a document that had been rejected (especially one approved by
            * one person and then rejected by another) with no control at all,
            * under a pill telling the reader to answer the refusal.
            *
            * `dismissDocument` is the move the database already sanctions for
            * this (0095, awaiting → draft): it keeps WHO refused and WHY on the
            * record, clears the sheet, and reopens the document as a draft to be
            * revised and sent again. Unlike a plain withdraw it is NOT blocked by
            * an earlier approval — a refused sheet is settled as "no", so there
            * is no approval to protect. Shown whenever there is a refusal to
            * answer; the plain-withdraw button below takes the unanswered case.
            */}
          {doc.state === 'awaiting' && mayAsk && refused.length > 0 && (
            <div className="doc-act">
              <button
                disabled={busy === 'answer'}
                {...inert('btn btn-small', async () => {
                  setBusy('answer');
                  setProblem(await dismissRejected(doc.id));
                  setBusy('');
                })}
              >
                {busy === 'answer' ? 'Reopening…' : 'Take it back to a draft'}
              </button>
              <span className="doc-hint">
                Keeps who refused and why on the record, and reopens the document so you can revise
                it and send it again.
              </span>
            </div>
          )}

          {/*
            * TAKING IT BACK, while it is still only a request.
            *
            * Shown only while NOBODY has signed and nobody has refused. After an
            * approval the document is evidence — somebody put their name to this
            * exact file — and the only honest move is a new version; after a
            * refusal the button above answers it instead. 0051 refuses it either
            * way; this is so the choice is never offered and then taken away.
            */}
          {doc.state === 'awaiting' && mayAsk && refused.length === 0 &&
            rows.filter((r) => r.signedAt && r.answer !== 'rejected').length === 0 && (
            <div className="doc-act">
              <button
                disabled={busy === 'back'}
                {...inert('btn btn-small', async () => {
                  setBusy('back');
                  setProblem(await withdraw(doc.id));
                  setBusy('');
                })}
              >
                {busy === 'back' ? 'Withdrawing…' : 'Withdraw the request'}
              </button>
              <span className="doc-hint">
                {rows.length === 1
                  ? 'Nobody has signed yet. It goes back to a draft you can edit, and the request is cancelled.'
                  : `Nobody has signed yet. It goes back to a draft you can edit, and all ${rows.length} requests are cancelled.`}
              </span>
            </div>
          )}

          {/*
            * CLOSE OUT A FULLY-APPROVED DOCUMENT THAT NEVER CLOSED ITSELF — the
            * safety net for 0087. Normally the last signature closes the document
            * out in the database (a_document_signs_itself_when_the_last_name_is_in),
            * so no button is needed. But a document whose final approval landed
            * before that trigger was live — or by any path that did not fire it —
            * sits at "awaiting" with every name already in and no way forward.
            * This button appears ONLY in that exact state: awaiting, at least one
            * approval, nobody outstanding and nobody refused. markDocumentSigned
            * makes the same awaiting → signed move, which the database allows a
            * manager/owner, so a control that would only be refused is not shown.
            */}
          {doc.state === 'awaiting' &&
            mayAsk &&
            rows.length > 0 &&
            rows.every((r) => r.signedAt && r.answer === 'approved') && (
              <div className="doc-act">
                <button
                  disabled={busy === 'marksigned'}
                  {...inert('btn btn-small', async () => {
                    setBusy('marksigned');
                    setProblem(await markSigned(doc.id));
                    setBusy('');
                  })}
                >
                  {busy === 'marksigned' ? 'Recording…' : 'Record it as signed off'}
                </button>
                <span className="doc-hint">
                  Everyone has approved and nobody refused, but this was never closed out. Record it
                  as signed off.
                </span>
              </div>
            )}

          {/*
            * THROWING IT AWAY, which is only ever possible from a draft.
            *
            * A document at `awaiting` has been put in front of named people, so
            * it is withdrawn first and deleted after — two acts, and the record
            * carries both. Confirmed because there is no undo: the row goes,
            * the file goes, and nothing keeps a copy.
            */}
          {doc.state === 'draft' && mayEditDoc && (
            <div className="doc-act">
              <button
                disabled={busy === 'bin'}
                {...inert('btn btn-small btn-danger', async () => {
                  const ok = await confirmDialog({
                    title: 'Delete this document?',
                    body: (
                      <>
                        “<b>{doc.title}</b>” — the file goes with it, and there is no undo.
                      </>
                    ),
                    confirmLabel: 'Delete',
                    tone: 'danger',
                  });
                  if (!ok) return;
                  setBusy('bin');
                  setProblem(await binIt(doc.id));
                  setBusy('');
                })}
              >
                {busy === 'bin' ? 'Deleting…' : received ? 'Delete this document' : 'Delete this draft'}
              </button>
              <span className="doc-hint">
                {received ? 'The file goes with it, and there is no undo.' : 'Nobody has been asked to sign it.'}
              </span>
            </div>
          )}

          {problem && (
            <p className="doc-problem" role="alert">
              {problem}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * WHO SIGNS THE CHARTER — shown on a charter draft once its PDF exists, in place
 * of the plain "Send for signature" button (#1).
 *
 * THE CHARTER NOW SIGNS THROUGH THE BOARD'S CONFIGURED SIGN-OFF, like every
 * other kind — configurable sign-offs R1, #445 (Vinnie, 1 Oct: "It does not
 * send it for team review, I have to add the names individually"). When the
 * board has set up a charter sign-off (its 'charter' override, or the board
 * default on the stage or slots model), the people named in it sign the charter:
 * the panel shows who they are, read-only, and sending is one click — there is
 * no hand-typed list to fill in and none to lose by leaving the screen. Who
 * signs is changed in Settings, where every other sign-off is set.
 *
 * FALLBACK — a board with NO sign-off configured keeps the charter's own list,
 * seeded with the sponsor from About (About holds no email, so the address is
 * asked for) and following a change to the sponsor until the manager edits it,
 * so a project that authorises its charter by its sponsor alone is unchanged.
 */
function CharterSignoff({ doc, onProblem }: { doc: Doc; onProblem: (why: string) => void }) {
  const sendForSignature = useApp((s) => s.sendDocumentForSignature);
  const sample = useSampleBoard();
  const inert = useSampleInert();
  const setView = useApp((s) => s.setView);
  const projectId = useApp((s) => s.projectId);
  const project = useApp((s) => s.projects.find((p) => p.id === projectId));
  const signoffPolicy = useApp((s) => s.meta.signoffPolicy);
  const ownerName = useApp((s) => s.meta.projectOwnerName);
  const ownerPosition = useApp((s) => s.meta.projectOwnerPosition);
  const ownerEmail = useApp((s) => s.meta.projectOwnerEmail);
  const access = useApp((s) => s.access);
  const roles = useApp((s) => s.roles);
  const projectRoles = useApp((s) => s.projectRoles);
  const profiles = useApp((s) => s.profiles);
  const [people, setPeople] = useState<Signatory[]>([]);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  // The charter's own sign-off rule — its kind override, or the board default.
  const charterPolicy = policyForType(signoffPolicy, 'charter');
  const onPolicy = usesStages(charterPolicy) || usesSlots(charterPolicy);
  const world: TheWorld = {
    owner: {
      name: (ownerName ?? '').trim(),
      position: (ownerPosition ?? '').trim(),
      email: (ownerEmail ?? '').trim(),
    },
    access,
    roles,
    projectRoles,
    profiles,
    projectId,
  };
  // Who the configured sign-off resolves to, grouped by stage for the preview —
  // the same grouping a raised sheet is drawn in, so the draft shows exactly who
  // will be asked and in what order.
  const signers = onPolicy ? sheetFor(charterPolicy, world) : [];
  const groups = sheetGroups(signers);

  // Seed from the sponsor, and keep following it until the manager touches the
  // list — the same pattern the About and phase editors use when a project loads.
  // Only the fallback (no configured sign-off) reads this.
  useEffect(() => {
    if (touched) return;
    setPeople(seedCharterSignatories(project?.sponsor));
  }, [project?.sponsor, touched]);

  // ── THE CONFIGURED SIGN-OFF — read-only signers, one-click send ──
  if (onPolicy) {
    return (
      <div className="charter-sign">
        <p className="charter-sign-lead">Who signs the charter</p>
        <p className="charter-sign-why">
          The charter is signed off by the people set for it in <em>Settings → Sign-off</em>, the
          same place every sign-off on this board is arranged. Change who signs there.
        </p>
        {signers.length === 0 ? (
          <p className="charter-sign-why">
            No one is named to sign the charter off yet.{' '}
            <button type="button" className="link" onClick={() => setView('settings')}>
              Set who signs in Settings
            </button>
            .
          </p>
        ) : (
          <div className="charter-sign-stages">
            {(groups.length > 1 ? groups : [null]).map((g, gi) => {
              const inGroup = g == null ? signers : signers.filter((r) => (r.step ?? '') === g);
              return (
                <div className="charter-sign-stage" key={g ?? gi}>
                  {g != null && (
                    <p className="charter-sign-stage-name">{stepLabel(g, signoffPolicy)}</p>
                  )}
                  <ul className="charter-sign-who">
                    {inGroup.map((r, i) => (
                      <li key={i}>
                        <span className="charter-sign-who-name">{r.who.name}</span>
                        {r.who.position.trim() && (
                          <span className="charter-sign-who-role">{r.who.position}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        )}
        <div className="charter-sign-foot">
          <button
            disabled={busy || signers.length === 0}
            {...inert('btn btn-small btn-primary', async () => {
              setBusy(true);
              onProblem(await sendForSignature(doc.id));
              setBusy(false);
            })}
          >
            {busy ? 'Asking…' : 'Send for sign-off'}
          </button>
          <span className="doc-hint">
            Once sent, these names are copied onto the charter and can&rsquo;t be changed — supersede
            it with a new version to change them.
          </span>
        </div>
      </div>
    );
  }

  // ── FALLBACK — no configured sign-off, the charter carries its own list ──
  const set = (i: number, patch: Partial<Signatory>) => {
    setTouched(true);
    setPeople((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  };
  const add = () => {
    setTouched(true);
    setPeople((xs) => [...xs, { name: '', position: '', email: '' }]);
  };
  const remove = (i: number) => {
    setTouched(true);
    setPeople((xs) => xs.filter((_, j) => j !== i));
  };

  return (
    <div className="charter-sign">
      <p className="charter-sign-lead">Who signs the charter</p>
      <p className="charter-sign-why">
        A charter is authorised by its <strong>sponsor</strong>, and whoever else must approve it — a
        steering chair, the PMO, finance. The sponsor from <em>Project details</em> is filled in;
        add anyone else who must sign. To route it through a set sign-off instead, set one up in{' '}
        <em>Settings → Sign-off</em>.
      </p>

      <div className="charter-sign-cols" aria-hidden="true">
        <span>Name <span className="req">*</span></span>
        <span>Role or title <span className="req">*</span></span>
        <span>Email <span className="req">*</span></span>
        <span />
      </div>
      <ul className="charter-sign-list">
        {people.map((s, i) => (
          <li key={i}>
            <input
              className="input"
              value={s.name}
              aria-label="Name"
              placeholder="Name"
              onChange={(e) => set(i, { name: e.target.value })}
              readOnly={sample.readOnly}
            />
            <input
              className="input"
              value={s.position}
              aria-label="Role or title"
              placeholder="Role or title"
              onChange={(e) => set(i, { position: e.target.value })}
              readOnly={sample.readOnly}
            />
            <input
              className="input"
              value={s.email ?? ''}
              type="email"
              aria-label="Email"
              placeholder="name@example.com"
              onChange={(e) => set(i, { email: e.target.value })}
              readOnly={sample.readOnly}
            />
            <button
              type="button"
              aria-label={`Remove ${s.name || 'this signatory'}`}
              {...inert('charter-sign-x', () => remove(i))}
            >
              <Icon name="close" size={13} />
            </button>
          </li>
        ))}
      </ul>
      <button type="button" {...inert('charter-sign-add', add)}>
        + Add signatory
      </button>

      <div className="charter-sign-foot">
        <button
          disabled={busy}
          {...inert('btn btn-small btn-primary', async () => {
            setBusy(true);
            onProblem(await sendForSignature(doc.id, people));
            setBusy(false);
          })}
        >
          {busy ? 'Asking…' : 'Send for sign-off'}
        </button>
        <span className="doc-hint">
          Once sent, these names are copied onto the charter and can&rsquo;t be changed — supersede it
          with a new version to change them.
        </span>
      </div>
    </div>
  );
}

/**
 * A NEW DOCUMENT — #5, the type picker.
 *
 * WHY THIS IS A PICKER, NOT A DROP-DOWN. "New document" used to open a flat
 * `<select>` of ten types in no order, so you had to already know the vocabulary
 * to choose. Here the types are grouped by what a person is doing — the documents
 * you produce, the ones you receive, and the project's initiation document — each
 * group naming a real category and each type saying what it is, picked in one
 * click. Nothing new to learn; it names what the app already had. The grouping and
 * the wording live in `documents.ts` (DOCUMENT_GROUPS / DOCUMENT_KIND_WHAT), so the
 * screen stays thin and the copy is testable.
 *
 * THE CHARTER IS REACHABLE HERE NOW. It never was — the old form left it off
 * because it is one per project and must not be made twice. The picker adds it,
 * on its own, and enforces the rule: if a charter already exists it offers to OPEN
 * it, and only otherwise starts one (opening the charter editor on the new draft).
 * It is never scoped to a sprint.
 *
 * The title and body are still written afterwards — a form that asks for
 * everything before it will save anything is one people abandon with nothing kept.
 */
export function NewDocument({
  onDone,
  onProblem,
  create,
  documents,
  onOpen,
}: {
  onDone: () => void;
  onProblem: (why: string) => void;
  create: (kind: DocumentKind, title: string, sprint?: string) => Promise<{ id: string; problem: string }>;
  documents: Doc[];
  onOpen: (id: string) => void;
}) {
  const sprints = useApp((s) => s.sprints);
  const [kind, setKind] = useState<DocumentKind>('requirements');
  const [title, setTitle] = useState('');
  /* Was the title WE suggested, or something the person typed? Only our own
     suggestion is cleared when they move to another type — a title they wrote by
     hand is theirs and is kept. */
  const [titleAuto, setTitleAuto] = useState(false);
  const [sprint, setSprint] = useState('');
  const [saving, setSaving] = useState(false);

  const existingCharter = documents.find((d) => d.kind === 'charter');
  const charterPicked = kind === 'charter';
  const charterExists = !!existingCharter;
  const scopeable = canScopeToSprint(kind);

  /* Picking the charter offers a sensible title straight away; every other kind
     is named by the person. Moving OFF the charter takes that suggestion back
     with it, so "Project charter" never lingers in the box for a requirements
     document (Vinnie, 16 Sep) — but only when it was our suggestion, never a
     title the person typed themselves. A charter that already exists is opened,
     not remade. */
  const pick = (k: DocumentKind) => {
    setKind(k);
    onProblem('');
    const next = titleForKind(k, title, titleAuto);
    setTitle(next.title);
    setTitleAuto(next.auto);
  };

  const submit = async () => {
    setSaving(true);
    const name = charterPicked ? title.trim() || 'Project charter' : title;
    const { id, problem } = await create(kind, name, scopeable ? sprint || undefined : undefined);
    setSaving(false);
    onProblem(problem);
    if (problem) return;
    /* CREATE TAKES YOU STRAIGHT TO THE DOCUMENT — no dropping back to the list to
       hunt for the row you just made (Vinnie, 16 Sep: an attachment made you name
       it, land on the list, then click it again to reach the upload). Every kind
       opens where its next action is: a received document on its upload, a charter
       on its editor, a generated one on its writing. The row is already in state
       (draftDocument inserts it on save), so it opens at once. */
    onOpen(id);
  };

  // Only the charter carries a state chip; the rest are picked freely.
  /* The tag beside a type's name in the picker. The charter keeps its own state
     chip ("Not started" / "Already created"); every other tagged type says how
     it comes to exist — Generated (the app assembles it) or Uploaded (you attach
     the file), see docSource. The tone drives the chip's tint (brass vs grey);
     both are the one chip shape (.dn-otag). */
  const chipFor = (
    k: DocumentKind
  ): { label: string; tone: 'generated' | 'uploaded' } | null => {
    if (k === 'charter')
      return { label: charterExists ? 'Already created' : 'Not started', tone: 'generated' };
    const src = docSource(k);
    if (src === 'generated') return { label: 'Generated', tone: 'generated' };
    if (src === 'uploaded') return { label: 'Uploaded', tone: 'uploaded' };
    return null;
  };

  /* THE OPTIONS ARE ONE RADIO GROUP, not eleven toggles — so the arrow keys move
     the choice the way every other radio group does, and a screen reader hears
     one "type of document" control. The kinds in reading order, across all three
     visual groups, is the order the arrows walk; a roving tabindex keeps the
     group a single tab stop. */
  const orderedKinds = DOCUMENT_GROUPS.flatMap((g) => g.kinds);
  const optRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const moveTo = (k: DocumentKind) => {
    pick(k);
    optRefs.current[k]?.focus();
  };
  const onOptionKeys = (e: React.KeyboardEvent) => {
    const i = orderedKinds.indexOf(kind);
    if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
      e.preventDefault();
      moveTo(orderedKinds[(i + 1) % orderedKinds.length]);
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
      e.preventDefault();
      moveTo(orderedKinds[(i - 1 + orderedKinds.length) % orderedKinds.length]);
    } else if (e.key === 'Home') {
      e.preventDefault();
      moveTo(orderedKinds[0]);
    } else if (e.key === 'End') {
      e.preventDefault();
      moveTo(orderedKinds[orderedKinds.length - 1]);
    }
  };

  /* LEAVING WITH A HAND-TYPED TITLE ASKS FIRST — the same draft guard the issue
     composer and the card modal give an unsaved draft (F5). Only a title the
     person typed counts; our own suggestion is not theirs to lose, so it closes
     without a prompt. */
  const tryClose = useCallback(async () => {
    if (title.trim() !== '' && !titleAuto) {
      const discard = await confirmDialog({
        title: 'Discard this document?',
        body: <>You&rsquo;ve started &ldquo;<b>{title.trim()}</b>&rdquo;. Leaving now won&rsquo;t keep it.</>,
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing',
        tone: 'caution',
      });
      if (!discard) return;
    }
    onDone();
  }, [title, titleAuto, onDone]);

  /* ESCAPE CLOSES THE PICKER — the same dismissal Cancel gives, through the same
     discard guard. Bound while the picker is mounted; nothing else on this screen
     claims Escape at the same time. (A confirm, once open, swallows Escape in the
     capture phase, so this never double-fires — ConfirmHost.) */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void tryClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [tryClose]);

  return (
    <div className="doc-new fd-form">
      <div className="fd-head">
        <div className="fd-head-main">
          <h2 className="fd-title">New document</h2>
          <p className="fd-sub">Select the type of document. Each is described below.</p>
        </div>
      </div>

      <section className="fd-sec">
        <h3 className="fd-sec-h">Type of Document</h3>
        <div className="dn-groups" role="radiogroup" aria-label="Type of Document" onKeyDown={onOptionKeys}>
        {DOCUMENT_GROUPS.map((group) => (
          <div className="dn-group" key={group.label}>
            <p className="dn-glabel">{group.label}</p>
            <p className="dn-gwhat">{group.what}</p>
            <div className={`dn-opts${group.kinds.length === 1 ? ' single' : ''}`}>
              {group.kinds.map((k) => {
                const chip = chipFor(k);
                return (
                <button
                  type="button"
                  key={k}
                  ref={(el) => {
                    optRefs.current[k] = el;
                  }}
                  className={`dn-opt${kind === k ? ' on' : ''}`}
                  role="radio"
                  aria-checked={kind === k}
                  tabIndex={kind === k ? 0 : -1}
                  onClick={() => pick(k)}
                >
                  <span className="dn-radio" aria-hidden="true" />
                  <span className="dn-otext">
                    <span className="dn-oname">
                      {DOCUMENT_KIND[k]}
                      {chip && (
                        <span className={`dn-otag${chip.tone === 'uploaded' ? ' uploaded' : ''}`}>
                          {chip.label}
                        </span>
                      )}
                    </span>
                    <span className="dn-owhat">{DOCUMENT_KIND_WHAT[k]}</span>
                  </span>
                </button>
                );
              })}
            </div>
          </div>
        ))}
        </div>
      </section>

      {charterPicked && charterExists ? (
        <p className="dn-standing">
          This project already has a charter. Open it to keep working on it.
        </p>
      ) : (
        <section className="fd-sec">
          <h3 className="fd-sec-h">Details</h3>
          <div className="fd-grid">
          <label className="stack">
            {/* Label and asterisk in ONE span so the flex-column `.stack` keeps
                them on one line — a bare text node + the `.req` span become two
                flex items and the * drops below (Vinnie, 13 Sep). */}
            <span>
              Title <span className="req" aria-hidden="true">*</span>
            </span>
            <input
              className="input"
              value={title}
              autoFocus
              aria-required="true"
              placeholder={TITLE_EXAMPLE[kind]}
              onChange={(e) => {
                setTitle(e.target.value);
                setTitleAuto(false);
              }}
              onKeyDown={(e) => {
                /* Enter commits from the title field — pick, type, and create
                   without reaching for the mouse. Held to the same guard the
                   button wears, so it never fires while disabled. */
                if (e.key === 'Enter' && !saving && (charterPicked || title.trim())) {
                  e.preventDefault();
                  void submit();
                }
              }}
            />
          </label>

          <label className="stack">
            <span>What it covers</span>
            {scopeable ? (
              <select className="input" value={sprint} onChange={(e) => setSprint(e.target.value)}>
                <option value="">Whole project</option>
                {sprints.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            ) : (
              /* Requirements are the release's, and a received or founding
                 document is the project's — none is scoped to a sprint, so the
                 choice is stated rather than offered. */
              <input className="input" value="Whole project" readOnly aria-readonly="true" />
            )}
          </label>
          </div>
        </section>
      )}

      <div className="fd-actions">
        {charterPicked && charterExists ? (
          <button className="btn btn-primary btn-small" onClick={() => onOpen(existingCharter!.id)}>
            Open the charter
          </button>
        ) : (
          <button
            className="btn btn-primary btn-small"
            disabled={saving || !(charterPicked ? true : title.trim())}
            onClick={submit}
          >
            {saving ? 'Saving…' : 'Create it'}
          </button>
        )}
        <button className="btn btn-small" onClick={() => void tryClose()}>
          Cancel
        </button>
      </div>
    </div>
  );
}

/**
 * THE DOCUMENT ITSELF — #200, widened for received files in 0111.
 *
 * A document sent for signature is written in Word and printed to PDF: it is not
 * typed into a browser, and the person asked to sign it has to be able to READ
 * it, print it and circulate it (Vinnie, 21 August). A received document is not
 * signed — it is handed to the project and kept — so it also takes a spreadsheet
 * or an image.
 *
 * READ WHERE IT SITS, OR DOWNLOADED. A PDF and an image are shown on the page and
 * a CSV is drawn as a table; a Word or Excel file cannot be shown, and this says
 * so plainly rather than offering a viewer that will show nothing — accepting
 * them was a deliberate decision with that cost attached, and hiding the cost
 * would be the one dishonest way to implement it.
 */
function TheFile({
  doc,
  mayReplace,
  onProblem,
}: {
  doc: Doc;
  mayReplace: boolean;
  onProblem: (why: string) => void;
}) {
  const upload = useApp((s) => s.uploadDocumentFile);
  const addressFor = useApp((s) => s.documentFileUrl);
  const sample = useSampleBoard();
  const inert = useSampleInert();
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [where, setWhere] = useState('');
  /* A CSV is read into a table here rather than shown in an <object>; the parsed
     sheet is held only while it is being read, and thrown away when the file
     changes or the reader is closed. */
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const [csvProblem, setCsvProblem] = useState('');

  const received = isReceived(doc.kind);
  const mode = viewAs(doc.fileType);

  /*
   * FETCHED WHEN IT IS ASKED FOR, and again if the file changes. The address is
   * short-lived by design — see the store — so holding one from the moment the
   * screen loaded would hand somebody a link that had already expired.
   */
  useEffect(() => {
    if (!reading || !doc.filePath) return;
    let stale = false;
    void addressFor(doc.id).then((url) => {
      if (!stale) setWhere(url);
    });
    return () => {
      stale = true;
    };
  }, [reading, doc.id, doc.filePath, addressFor]);

  /*
   * A CSV IS READ INTO A TABLE. The signed address is fetched above; this pulls
   * the text over it and parses it with the same reader the importer uses, so
   * what a person previews is exactly what an import would see. Reset whenever
   * the file changes.
   */
  useEffect(() => {
    setSheet(null);
    setCsvProblem('');
    if (!reading || mode !== 'table' || !where) return;
    let stale = false;
    void fetch(where)
      .then((r) => r.text())
      .then((text) => {
        if (!stale) setSheet(parseCsv(text));
      })
      .catch(() => {
        if (!stale) setCsvProblem('That file could not be read here. Download it to open it.');
      });
    return () => {
      stale = true;
    };
  }, [reading, mode, where, doc.fileHash]);

  const take = async (chosen: File | undefined) => {
    if (!chosen) return;
    setBusy(true);
    onProblem(await upload(doc.id, chosen));
    setBusy(false);
  };

  if (!doc.filePath) {
    return mayReplace ? (
      <label {...inert('doc-drop', () => {})}>
        <strong>{busy ? 'Uploading…' : 'Choose the document'}</strong>
        {received
          ? 'PDF, Word, Excel, CSV or an image, up to 25 MB. It is kept on the project for the record.'
          : doc.kind === 'test'
          ? 'PDF, Word or Excel, up to 25 MB. A PDF or Word one can be sent for sign-off; an Excel one is kept for the record.'
          : 'PDF or Word, up to 25 MB. It cannot be sent for signature without one.'}
        <input
          type="file"
          accept={acceptFor(doc.kind)}
          disabled={busy || sample.readOnly}
          onChange={(e) => void take(e.target.files?.[0])}
        />
      </label>
    ) : (
      <p className="doc-none-yet">No file has been uploaded yet.</p>
    );
  }

  const onThePage = readsOnThePage(doc);
  const size = doc.fileSize ? `${Math.max(1, Math.round(doc.fileSize / 1024))} KB` : '';

  return (
    <div className="doc-file">
      <div className="doc-file-line">
        <span className="doc-file-name">{doc.fileName}</span>
        <span className="doc-file-meta">
          {[fileWhat(doc.fileType), size].filter(Boolean).join(' · ')}
        </span>
        {/* GLYPHS, NOT SENTENCES. Three phrases sat on the same line as the
            file's own name and pushed it out of the row on a narrow screen.
            Each keeps its words for anyone who cannot see the drawing — the
            title shows them on hover, the label reads them aloud — and each
            keeps a 44px target regardless of the 16px icon inside it. */}
        <span className="doc-file-acts">
          {onThePage && (
            <button
              className="btn-icon icon-act"
              title={reading ? 'Close it' : 'Read it here'}
              aria-label={reading ? 'Close it' : 'Read it here'}
              aria-pressed={reading}
              onClick={() => setReading(!reading)}
            >
              <Icon name={reading ? 'close' : 'eye'} />
            </button>
          )}
          <a
            className="btn-icon icon-act"
            title={onThePage ? 'Open or print' : 'Download to read it'}
            aria-label={onThePage ? 'Open or print' : 'Download to read it'}
            href={where || undefined}
            target="_blank"
            rel="noreferrer"
            onClick={async (e) => {
              /* The address is fetched on the way out rather than held, for the
                 same reason the reader fetches its own: a link minted when the
                 screen loaded has expired by the time anybody presses it. */
              if (where) return;
              e.preventDefault();
              const url = await addressFor(doc.id);
              if (url) window.open(url, '_blank', 'noreferrer');
              else onProblem('That file could not be opened. Try again.');
            }}
          >
            <Icon name={onThePage ? 'printer' : 'download'} />
          </a>
          {mayReplace && (
            /* THE LABEL ON THE INPUT, NOT ON THE LABEL. The words used to BE the
               label's content, which is what named the file input inside it.
               Replacing them with a drawing left the input with no name at all,
               so a screen reader announced an unlabelled file picker. */
            <label {...inert('btn-icon icon-act doc-replace', () => {}, 'Replace it')}>
              <Icon name="upload" />
              <input
                type="file"
                aria-label="Replace it"
                accept={acceptFor(doc.kind)}
                disabled={busy || sample.readOnly}
                onChange={(e) => void take(e.target.files?.[0])}
              />
            </label>
          )}
        </span>
      </div>

      {!onThePage && (
        <p className="doc-file-warn">
          {`${fileWhat(doc.fileType) || 'This file'} cannot be read on this page — download it to open it in its own app.`}
        </p>
      )}

      {reading && onThePage && (
        <div className="doc-viewer">
          {!where ? (
            <p className="doc-file-warn">Fetching it…</p>
          ) : mode === 'image' ? (
            <img className="doc-view-image" src={where} alt={doc.fileName} />
          ) : mode === 'table' ? (
            csvProblem ? (
              <p className="doc-file-warn">{csvProblem}</p>
            ) : !sheet ? (
              <p className="doc-file-warn">Reading it…</p>
            ) : (
              <CsvTable sheet={sheet} />
            )
          ) : (
            <object data={where} type="application/pdf" aria-label={doc.fileName}>
              <p>
                This browser will not show a PDF here.{' '}
                <a href={where} target="_blank" rel="noreferrer">
                  Open it in a new tab
                </a>
                .
              </p>
            </object>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A CSV DRAWN AS A TABLE, for reference — not the importer.
 *
 * A long file is capped: the point here is to glance at what was received, and a
 * ten-thousand-row sheet in the DOM helps nobody. The cap is said out loud so
 * the reader knows there is more, and the whole file is a download away.
 */
function CsvTable({ sheet }: { sheet: Sheet }) {
  const CAP = 100;
  const shown = sheet.rows.slice(0, CAP);
  return (
    <div className="doc-view-table">
      <table>
        <thead>
          <tr>
            {sheet.headers.map((h, i) => (
              <th key={i}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown.map((r, i) => (
            <tr key={i}>
              {r.map((c, j) => (
                <td key={j}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {sheet.rows.length > CAP && (
        <p className="doc-view-more">
          Showing the first {CAP} of {sheet.rows.length} rows — download the file for all of it.
        </p>
      )}
    </div>
  );
}
